// ============================================
// P3 支付线（F-08 / B1）：素材库页面
// - 免费/已授权：直接下载；付费未购：锁定 + 单购价 → 支付弹窗；付费无单价：引导开通套餐
// - B1 增强：关键词搜索 / 类型筛选 / 状态筛选（免费·待购买·我的已购）/ 排序 / 预览弹层
// - 支持外部入口 ?kw=xxx 预填搜索（如官网素材广场「前往购买」）
// ============================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Button, Tag, Typography, Space, Empty, Spin, message, Tooltip, Input, Select, Modal } from 'antd';
import { DownloadOutlined, ShoppingCartOutlined, ReloadOutlined, LockOutlined, CrownOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchMaterialList, type MaterialItem } from '../api/material';
import { usePayIntentStore } from '../components/payment/payIntent';

const { Text, Paragraph } = Typography;

const ASSET_LABEL: Record<string, string> = { dashboard: '大屏模板', scene: '3D场景', component: '组件包' };

/** 推荐角标（右上角蓝色拉幅，形状参考用户提供的旗帜样式）：由 cover 容器相对定位承载 */
function RecoRibbon() {
  return (
    <div
      data-testid="material-reco-ribbon"
      style={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64, overflow: 'hidden', pointerEvents: 'none', zIndex: 2, borderTopRightRadius: 6 }}
    >
      <div
        style={{
          position: 'absolute', top: 14, right: -30, width: 110, transform: 'rotate(45deg)',
          background: '#1677ff', color: '#fff', fontSize: 11, lineHeight: '17px', textAlign: 'center',
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)', letterSpacing: 2,
        }}
      >
        推荐
      </div>
    </div>
  );
}

export default function MaterialLibrary() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const openPay = usePayIntentStore((s) => s.openPay);
  const [list, setList] = useState<MaterialItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [kw, setKw] = useState(() => searchParams.get('kw') || '');
  const [assetType, setAssetType] = useState<string>('all');
  const [state, setState] = useState<'all' | 'free' | 'locked' | 'owned'>('all');
  const [sort, setSort] = useState<'newest' | 'name'>('newest');
  const [preview, setPreview] = useState<MaterialItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { list: items } = await fetchMaterialList();
      setList(items || []);
    } catch (e: any) {
      message.error(`素材库加载失败：${e?.response?.data?.message || e?.message || ''}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    let arr = list;
    if (kw.trim()) {
      const k = kw.trim();
      arr = arr.filter((m) => m.name.includes(k) || (m.desc || '').includes(k) || (m.categoryTags || []).some((t) => t.includes(k)));
    }
    if (assetType !== 'all') arr = arr.filter((m) => m.assetType === assetType);
    if (state === 'free') arr = arr.filter((m) => m.priceType === 'free');
    if (state === 'locked') arr = arr.filter((m) => m.locked);
    if (state === 'owned') arr = arr.filter((m) => m.priceType === 'paid' && m.entitled);
    if (sort === 'name') arr = [...arr].sort((a, b) => a.name.localeCompare(b.name, 'zh'));
    return arr;
  }, [list, kw, assetType, state, sort]);

  /**
   * 「为你推荐」V1 行为规则（无埋点版；2026-10-04 起）：
   * 1) 候选 = 未拥有素材（locked）；2) 排序分：与已购同类型 +2、标签重合每个 +1、免费 +0.5；
   * 3) 不足 6 个时用"最新素材"补齐；最多 9 个。
   * （V2 可接入浏览/下载行为埋点做协同过滤，接口不变）
   */
  const recommend = useMemo(() => {
    if (list.length === 0) return [];
    const owned = list.filter((m) => !m.locked);
    const ownedTypes = new Set(owned.map((m) => m.assetType));
    const ownedTags = new Set(owned.flatMap((m) => m.categoryTags || []));
    const score = (m: MaterialItem) => {
      let s = 0;
      if (ownedTypes.has(m.assetType)) s += 2;
      for (const t of m.categoryTags || []) if (ownedTags.has(t)) s += 1;
      if (m.priceType === 'free') s += 0.5;
      return s;
    };
    const picked = list.filter((m) => m.locked).sort((a, b) => score(b) - score(a)).slice(0, 9);
    if (picked.length < 6) {
      for (const m of list) {
        if (picked.length >= 9) break;
        if (!picked.includes(m)) picked.push(m);
      }
    }
    return picked;
  }, [list]);

  const recoIds = useMemo(() => new Set(recommend.map((m) => m.id)), [recommend]);
  /** 默认视图（无搜索/筛选/排序）时：推荐素材置顶展示（不重复出现于后方列表）；筛选时仅展示过滤结果 */
  const defaultView = !kw.trim() && assetType === 'all' && state === 'all' && sort === 'newest';
  const displayList = useMemo(() => {
    if (!defaultView) return filtered;
    const reco = filtered.filter((m) => recoIds.has(m.id));
    const rest = filtered.filter((m) => !recoIds.has(m.id));
    return [...reco, ...rest];
  }, [filtered, defaultView, recoIds]);

  const onBuy = (m: MaterialItem) => {
    message.info(`请在支付窗口完成「${m.name}」购买`);
    openPay(
      { orderType: 'material', productRef: m.sourceId, amount: Number(m.price), subject: `素材-${m.name}` },
      () => { void load(); setPreview(null); },
    );
  };

  const onDownload = (m: MaterialItem) => {
    if (!m.downloadUrl) { message.warning('下载地址未就绪，请稍后重试'); return; }
    window.open(m.downloadUrl, '_blank', 'noopener');
  };

  /** 操作区（卡片与预览弹层共用） */
  const actions = (m: MaterialItem, inModal = false) => {
    if (!m.locked) {
      return (
        <Button type={inModal ? 'primary' : 'link'} size="small" icon={<DownloadOutlined />} onClick={() => onDownload(m)}
          {...(!inModal ? { 'data-testid': `material-download-${m.sourceId}` } : {})}>
          下载
        </Button>
      );
    }
    if (m.price != null && Number(m.price) > 0) {
      return (
        <Button type="primary" size="small" icon={<ShoppingCartOutlined />} onClick={() => onBuy(m)}
          data-testid={inModal ? undefined : `material-buy-${m.sourceId}`}>
          购买 ¥{Number(m.price).toFixed(2)}
        </Button>
      );
    }
    return (
      <Tooltip title="该素材需订阅套餐后使用">
        <Button size="small" icon={<CrownOutlined />} onClick={() => navigate('/account')}
          data-testid={inModal ? undefined : `material-plan-${m.sourceId}`}>
          开通套餐
        </Button>
      </Tooltip>
    );
  };

  const total = list.length;
  const ownedCount = list.filter((m) => m.priceType === 'paid' && m.entitled).length;

  return (
    <div style={{ padding: 16 }}>
      <div className="mb-4 flex items-center justify-between" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <h2 className="text-lg font-bold" style={{ margin: 0 }}>素材库</h2>
          <Text type="secondary" style={{ fontSize: 12 }}>
            共 {total} 个 · 已购 {ownedCount} 个（付费素材购买后立即可下载；订阅套餐同样解锁对应素材）
          </Text>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={() => void load()}>刷新</Button>
      </div>

      {/* 2026-10-04（用户第二轮）：推荐并入主列表置顶（右上角蓝色"推荐"角标），不再单独一行；卡片样式对齐「我的资产」 */}

      {/* B1：搜索 / 筛选 / 排序 */}
      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          allowClear
          placeholder="搜索名称 / 描述 / 标签"
          value={kw}
          onChange={(e) => setKw(e.target.value)}
          style={{ width: 240 }}
          data-testid="material-search"
        />
        <Select
          style={{ width: 130 }} value={assetType} onChange={setAssetType}
          data-testid="material-type-filter"
          options={[
            { value: 'all', label: '全部类型' },
            { value: 'dashboard', label: '大屏模板' },
            { value: 'scene', label: '3D场景' },
            { value: 'component', label: '组件包' },
          ]}
        />
        <Select
          style={{ width: 140 }} value={state} onChange={(v) => setState(v)}
          data-testid="material-state-filter"
          options={[
            { value: 'all', label: '全部状态' },
            { value: 'free', label: '免费' },
            { value: 'locked', label: '待购买' },
            { value: 'owned', label: '我的已购' },
          ]}
        />
        <Select
          style={{ width: 120 }} value={sort} onChange={(v) => setSort(v)}
          data-testid="material-sort"
          options={[
            { value: 'newest', label: '最新优先' },
            { value: 'name', label: '按名称' },
          ]}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>筛选结果 {filtered.length} 个</Text>
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
      ) : filtered.length === 0 ? (
        <Empty description={total === 0 ? '素材库为空（可在官网素材广场发布后同步）' : '没有符合条件的素材'} />
      ) : (
        // 卡片样式对齐「我的资产」（小卡片 240+、圆角、紧凑信息），推荐项置顶并带角标
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {displayList.map((m) => {
            const isReco = defaultView && recoIds.has(m.id);
            return (
              <Card
                key={m.id}
                hoverable
                size="small"
                data-testid={`material-card-${m.sourceId}`}
                data-locked={m.locked ? 'true' : 'false'}
                data-reco={isReco ? 'true' : 'false'}
                styles={{ body: { padding: 10 } }}
                cover={(
                  <div
                    onClick={() => setPreview(m)}
                    title="点击预览"
                    data-testid={`material-cover-${m.sourceId}`}
                    style={{ height: 120, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'zoom-in', position: 'relative' }}
                  >
                    {m.coverImageUrl
                      ? <img alt={m.name} src={m.coverImageUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <Text type="secondary" style={{ fontSize: 12 }}>无封面</Text>}
                    {isReco && <RecoRibbon />}
                    <div style={{ position: 'absolute', right: 6, bottom: 6, background: 'rgba(0,0,0,0.45)', borderRadius: 4, padding: '2px 6px', color: '#fff', fontSize: 11 }}>
                      <EyeOutlined /> 预览
                    </div>
                  </div>
                )}
                actions={[actions(m)]}
              >
                <Space orientation="vertical" size={4} style={{ width: '100%' }}>
                  <Space size={6}>
                    <Text strong ellipsis style={{ fontSize: 13, maxWidth: 190 }} title={m.name}>{m.name}</Text>
                    {m.locked && <LockOutlined style={{ color: '#faad14', fontSize: 12 }} />}
                  </Space>
                  <Space size={4} wrap>
                    <Tag style={{ fontSize: 11, lineHeight: '18px', margin: 0 }}>{ASSET_LABEL[m.assetType] || m.assetType}</Tag>
                    {m.priceType === 'paid'
                      ? <Tag color="gold" style={{ fontSize: 11, lineHeight: '18px', margin: 0 }}>付费{m.price != null ? ` ¥${m.price}` : ''}</Tag>
                      : <Tag color="green" style={{ fontSize: 11, lineHeight: '18px', margin: 0 }}>免费</Tag>}
                    {m.watermark && <Tag color="orange" style={{ fontSize: 11, lineHeight: '18px', margin: 0 }}>含水印</Tag>}
                  </Space>
                  <Text type="secondary" ellipsis style={{ fontSize: 11 }} title={m.desc || ''}>{m.desc || '—'}</Text>
                </Space>
              </Card>
            );
          })}
        </div>
      )}

      {/* B1：预览弹层 */}
      <Modal
        open={!!preview}
        onCancel={() => setPreview(null)}
        footer={null}
        width={640}
        title={preview?.name}
        data-testid="material-preview-modal"
      >
        {preview && (
          <div>
            <div style={{ height: 280, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: 8, marginBottom: 12 }}>
              {preview.coverImageUrl
                ? <img alt={preview.name} src={preview.coverImageUrl} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                : <Text type="secondary">无封面</Text>}
            </div>
            <Paragraph type="secondary" style={{ fontSize: 13 }}>{preview.desc || '暂无描述'}</Paragraph>
            <Space size={4} wrap style={{ marginBottom: 16 }}>
              <Tag>{ASSET_LABEL[preview.assetType] || preview.assetType}</Tag>
              {preview.priceType === 'paid'
                ? <Tag color="gold">付费{preview.price != null ? ` ¥${preview.price}` : ''}{preview.packageId ? `·${preview.packageId}` : ''}</Tag>
                : <Tag color="green">免费</Tag>}
              {preview.watermark && <Tag color="orange">含水印</Tag>}
              {(preview.categoryTags || []).map((t) => <Tag key={t} color="blue">{t}</Tag>)}
              {preview.entitled && preview.priceType === 'paid' && <Tag color="success">已授权</Tag>}
              {preview.locked && <Tag color="warning">未购买</Tag>}
            </Space>
            <div style={{ textAlign: 'right' }}>{actions(preview, true)}</div>
          </div>
        )}
      </Modal>
    </div>
  );
}
